import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Mail } from "lucide-react";
import { Logo } from "../components/Logo";
import { hwApi, hwErr, HW_CATEGORIES } from "./hwApi";
import "../test/testTheme.css";

const BLANK = { name: "", email: "", password: "", category: "", mobile: "", parent_email: "" };

// Hoisted to module scope — defining a component inside render gives it a new identity on
// every keystroke, which remounts the <select> and instantly closes the open dropdown.
const CatSelect = ({ value, onChange, testid }) => (
  <select required value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
    <option value="">Select class / exam</option>
    {HW_CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
  </select>
);

export default function HomeworkAuth() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | otp | register | reset
  const [login, setLogin] = useState({ email: "", password: "" });
  const [form, setForm] = useState(BLANK);
  const [otp, setOtp] = useState({ email: "", code: "", sent: false, isNew: false, name: "", category: "" });
  const [reset, setReset] = useState({ email: "", code: "", password: "", sent: false });
  const [resendIn, setResendIn] = useState(0);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const switchMode = (m) => { setMode(m); setError(""); setNotice(""); };
  const ok = (data) => navigate("/homework", { state: { user: data } });

  const run = async (fn) => {
    setError("");
    setLoading(true);
    try { await fn(); } catch (err) { setError(hwErr(err)); } finally { setLoading(false); }
  };

  const submitLogin = (e) => { e.preventDefault(); run(async () => ok((await hwApi.post("/auth/login", login)).data)); };
  const submitRegister = (e) => { e.preventDefault(); run(async () => ok((await hwApi.post("/auth/register", form)).data)); };

  const sendOtp = (e) => {
    e?.preventDefault();
    run(async () => {
      await hwApi.post("/auth/otp/request", { email: otp.email });
      setOtp((c) => ({ ...c, sent: true }));
      setResendIn(60);
      setNotice("Code sent! Check your inbox (and spam folder).");
    });
  };

  const verifyOtp = (e) => {
    e.preventDefault();
    run(async () => {
      try {
        const { data } = await hwApi.post("/auth/otp/verify", { email: otp.email, code: otp.code });
        ok(data);
      } catch (err) {
        if (err.response?.status === 428) {
          setOtp((c) => ({ ...c, isNew: true }));
          setNotice("Email verified! Add your name and class/exam to finish creating your account.");
        } else { throw err; }
      }
    });
  };

  const registerOtp = (e) => {
    e.preventDefault();
    run(async () => ok((await hwApi.post("/auth/otp/register", {
      name: otp.name, email: otp.email, code: otp.code, category: otp.category })).data));
  };

  const sendReset = (e) => {
    e.preventDefault();
    run(async () => {
      await hwApi.post("/auth/reset/request", { email: reset.email });
      setReset((c) => ({ ...c, sent: true }));
      setNotice("If this email is registered, a reset code is on its way.");
    });
  };

  const confirmReset = (e) => {
    e.preventDefault();
    run(async () => {
      await hwApi.post("/auth/reset/confirm", { email: reset.email, code: reset.code, password: reset.password });
      setNotice("Password updated! Log in with your new password.");
      setMode("login");
      setLogin({ email: reset.email, password: "" });
    });
  };

  return (
    <div className="tp-shell">
      <div className="tp-topbar"><div className="tp-topbar-inner">
        <a className="tp-brand" href="/" data-testid="hw-auth-brand"><Logo size={24} /><span>PRANJAL SIR <b>PHYSICS MENTOR</b></span></a>
        <a className="tp-back" href="/">← Back to main site</a>
      </div></div>
      <div className="tp-wrap" style={{ maxWidth: 620, padding: "50px 22px 70px" }}>
        <div className="tp-eyebrow" style={{ textAlign: "center" }}>CLASS 6–12 · JEE · NEET</div>
        <h1 style={{ textAlign: "center", fontSize: 26, margin: "6px 0 2px", color: "#0b2545" }} data-testid="hw-auth-tagline">NOT AI. ONLY PRANJAL SIR.</h1>
        <p className="tp-muted" style={{ textAlign: "center", marginBottom: 18 }}>Your Personal Physics Mentor, For Your Benefit. Practice Physics. Submit Your Homework. Get Personally Checked by Pranjal Sir.</p>
        <div className="admin-tabs" style={{ justifyContent: "center", marginBottom: 18 }} data-testid="hw-auth-tabs">
          <button className={mode === "login" ? "active" : ""} onClick={() => switchMode("login")} data-testid="hw-tab-login">Password</button>
          <button className={mode === "otp" ? "active" : ""} onClick={() => switchMode("otp")} data-testid="hw-tab-otp">Email OTP</button>
          <button className={mode === "register" ? "active" : ""} onClick={() => switchMode("register")} data-testid="hw-tab-register">Register</button>
          <button className={mode === "reset" ? "active" : ""} onClick={() => switchMode("reset")} data-testid="hw-tab-reset">Forgot?</button>
        </div>
        <div className="tp-panel">
          {error && <p className="tp-error" role="alert" data-testid="hw-auth-error">{error}</p>}
          {notice && <p className="tp-pill ok" style={{ marginBottom: 12 }} data-testid="hw-auth-notice">{notice}</p>}

          {mode === "login" && (
            <form onSubmit={submitLogin} data-testid="hw-login-form">
              <h2 className="tp-h2">Welcome back</h2>
              <p className="tp-muted" style={{ marginBottom: 18 }}>Log in to see your homework, submissions and marks.</p>
              <label className="tp-label">EMAIL<input type="email" required value={login.email} onChange={(e) => setLogin((c) => ({ ...c, email: e.target.value }))} placeholder="you@example.com" data-testid="hw-login-email" /></label>
              <label className="tp-label">PASSWORD<input type="password" required value={login.password} onChange={(e) => setLogin((c) => ({ ...c, password: e.target.value }))} placeholder="Your password" data-testid="hw-login-password" /></label>
              <button className="tp-btn" style={{ width: "100%" }} disabled={loading} type="submit" data-testid="hw-login-submit">{loading ? "Logging in…" : "LOG IN"} <ArrowRight size={16} /></button>
              <p className="tp-note" style={{ marginTop: 14, textAlign: "center" }}>New here? <button type="button" className="tp-back" style={{ background: "none", border: "none", cursor: "pointer" }} onClick={() => switchMode("register")} data-testid="hw-goto-register">Create your account →</button></p>
            </form>
          )}

          {mode === "otp" && (
            <form onSubmit={otp.isNew ? registerOtp : otp.sent ? verifyOtp : sendOtp} data-testid="hw-otp-form">
              <h2 className="tp-h2">Login with a code</h2>
              <p className="tp-muted" style={{ marginBottom: 18 }}>We email you a 6-digit code — this also verifies your email. New here? Your account is created after verification.</p>
              <label className="tp-label">EMAIL<input type="email" required value={otp.email} disabled={otp.sent} onChange={(e) => setOtp((c) => ({ ...c, email: e.target.value }))} placeholder="you@example.com" data-testid="hw-otp-email" /></label>
              {otp.sent && (
                <label className="tp-label">6-DIGIT CODE<input required value={otp.code} disabled={otp.isNew} onChange={(e) => setOtp((c) => ({ ...c, code: e.target.value.replace(/\D/g, "").slice(0, 6) }))} placeholder="••••••" inputMode="numeric" autoFocus data-testid="hw-otp-code" /></label>
              )}
              {otp.isNew && (
                <>
                  <label className="tp-label">FULL NAME<input required value={otp.name} onChange={(e) => setOtp((c) => ({ ...c, name: e.target.value }))} placeholder="Your full name" data-testid="hw-otp-name" /></label>
                  <label className="tp-label">CLASS / EXAM<CatSelect value={otp.category} onChange={(v) => setOtp((c) => ({ ...c, category: v }))} testid="hw-otp-category" /></label>
                </>
              )}
              <button className="tp-btn" style={{ width: "100%" }} disabled={loading} type="submit" data-testid={otp.isNew ? "hw-otp-register" : otp.sent ? "hw-otp-verify" : "hw-otp-send"}>
                {loading ? "Please wait…" : otp.isNew ? <>CREATE ACCOUNT <ArrowRight size={16} /></> : otp.sent ? <>VERIFY & LOG IN <ArrowRight size={16} /></> : <>EMAIL ME A CODE <Mail size={16} /></>}
              </button>
              {otp.sent && !otp.isNew && (
                <p className="tp-note" style={{ marginTop: 12, textAlign: "center" }}>
                  {resendIn > 0 ? <span data-testid="hw-otp-timer">Resend in {resendIn}s</span> : (
                    <button type="button" className="tp-back" style={{ background: "none", border: "none", cursor: "pointer" }} onClick={() => setOtp((c) => ({ ...c, sent: false, code: "", isNew: false }))} data-testid="hw-otp-resend">Send a new code</button>)}
                </p>
              )}
            </form>
          )}

          {mode === "register" && (
            <form onSubmit={submitRegister} data-testid="hw-register-form">
              <h2 className="tp-h2">Create your account</h2>
              <p className="tp-muted" style={{ marginBottom: 18 }}>Daily homework, handwritten uploads, and personal evaluation by Pranjal Sir.</p>
              <label className="tp-label">FULL NAME *<input required value={form.name} onChange={(e) => setForm((c) => ({ ...c, name: e.target.value }))} placeholder="Your full name" data-testid="hw-reg-name" /></label>
              <div className="tp-grid-2">
                <label className="tp-label">EMAIL *<input type="email" required value={form.email} onChange={(e) => setForm((c) => ({ ...c, email: e.target.value }))} placeholder="you@example.com" data-testid="hw-reg-email" /></label>
                <label className="tp-label">PASSWORD (MIN 8 CHARS) *<input type="password" required minLength={8} value={form.password} onChange={(e) => setForm((c) => ({ ...c, password: e.target.value }))} placeholder="Choose a password" data-testid="hw-reg-password" /></label>
              </div>
              <div className="tp-grid-2">
                <label className="tp-label">CLASS / EXAM *<CatSelect value={form.category} onChange={(v) => setForm((c) => ({ ...c, category: v }))} testid="hw-reg-category" /></label>
                <label className="tp-label">MOBILE (OPTIONAL)<input value={form.mobile} onChange={(e) => setForm((c) => ({ ...c, mobile: e.target.value }))} placeholder="10-digit number" inputMode="numeric" data-testid="hw-reg-mobile" /></label>
              </div>
              <label className="tp-label">PARENT'S EMAIL (OPTIONAL — weekly progress report)<input type="email" value={form.parent_email} onChange={(e) => setForm((c) => ({ ...c, parent_email: e.target.value }))} placeholder="parent@example.com" data-testid="hw-reg-parent-email" /></label>
              <button className="tp-btn" style={{ width: "100%" }} disabled={loading} type="submit" data-testid="hw-register-submit">{loading ? "Creating…" : "CREATE MY ACCOUNT"} <ArrowRight size={16} /></button>
            </form>
          )}

          {mode === "reset" && (
            <form onSubmit={reset.sent ? confirmReset : sendReset} data-testid="hw-reset-form">
              <h2 className="tp-h2">Reset password</h2>
              <p className="tp-muted" style={{ marginBottom: 18 }}>We email you a 6-digit reset code.</p>
              <label className="tp-label">EMAIL<input type="email" required value={reset.email} disabled={reset.sent} onChange={(e) => setReset((c) => ({ ...c, email: e.target.value }))} data-testid="hw-reset-email" /></label>
              {reset.sent && (
                <>
                  <label className="tp-label">6-DIGIT CODE<input required value={reset.code} onChange={(e) => setReset((c) => ({ ...c, code: e.target.value.replace(/\D/g, "").slice(0, 6) }))} inputMode="numeric" data-testid="hw-reset-code" /></label>
                  <label className="tp-label">NEW PASSWORD (MIN 8 CHARS)<input type="password" required minLength={8} value={reset.password} onChange={(e) => setReset((c) => ({ ...c, password: e.target.value }))} data-testid="hw-reset-password" /></label>
                </>
              )}
              <button className="tp-btn" style={{ width: "100%" }} disabled={loading} type="submit" data-testid={reset.sent ? "hw-reset-confirm" : "hw-reset-send"}>{loading ? "Please wait…" : reset.sent ? "SET NEW PASSWORD" : "EMAIL ME A RESET CODE"}</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
